import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import DealCard from "@/components/DealCard";
import ProductCard from "@/components/ProductCard";
import Marquee from "@/components/Marquee";
import WaitlistForm from "@/components/WaitlistForm";
import { dispensaries as dApi, deals as dealsApi, products as pApi } from "@/lib/api";
import { useStateCtx } from "@/context/StateContext";
import {
  Flame, Sparkles, ArrowRight, ChevronRight, Star, MapPin, Award, Trophy, Zap, ScanLine,
} from "lucide-react";

export default function Landing() {
  const { stateCode } = useStateCtx();
  const [deals, setDeals] = useState([]);
  const [products, setProducts] = useState([]);
  const [disps, setDisps] = useState([]);

  useEffect(() => {
    const stateParam = stateCode === "ALL" ? undefined : stateCode;
    dealsApi.list({ state: stateParam }).then(setDeals).catch(() => {});
    pApi.list({ state: stateParam, sort: "value", limit: 6 }).then(setProducts).catch(() => {});
    dApi.list({ state: stateParam }).then(setDisps).catch(() => {});
  }, [stateCode]);

  return (
    <div className="min-h-screen overflow-x-hidden">
      <NavBar />

      {/* HERO — cream bg with floating blobs */}
      <section className="relative overflow-hidden">
        <div className="blob" style={{ width: 480, height: 480, background: "#15FF73", top: -120, right: -100 }} />
        <div className="blob" style={{ width: 380, height: 380, background: "#FF1A8C", bottom: -180, left: -120, animationDelay: "-6s" }} />
        <div className="blob" style={{ width: 320, height: 320, background: "#00E0F5", top: 200, left: "40%", animationDelay: "-12s" }} />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-12 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-2 mb-7">
              <span className="sticker sticker-green"><span className="pulse-dot mr-1" /> Live · {stateCode}</span>
              <span className="sticker sticker-magenta">All Gas, No Breaks</span>
              <span className="sticker sticker-cyan" style={{ transform: "rotate(2deg)" }}>21+</span>
            </div>
            <h1 className="font-display font-extrabold text-[56px] sm:text-[80px] lg:text-[112px] leading-[0.88] tracking-[-0.04em] mb-6">
              Find the <span className="text-rainbow">gas</span>.<br />
              Skip the <span className="squiggle">mid</span>.
            </h1>
            <p className="text-lg text-[#0B0F0C]/70 max-w-xl mb-8 leading-relaxed">
              Real deals. Real reviews. Real-time prices from your block. Smoke tested by the people, scored by the math.
              <span className="font-script text-2xl text-[#FF1A8C] ml-2">— oowee.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/discover" data-testid="hero-cta-discover" className="btn-x btn-primary">
                Find the gas <ArrowRight size={16} strokeWidth={2.4} />
              </Link>
              <Link to="/concierge" data-testid="hero-cta-concierge" className="btn-x btn-magenta">
                <Sparkles size={16} strokeWidth={2.4} /> Ask Bud
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-7 flex-wrap text-sm">
              <div className="flex items-center gap-2"><div className="text-3xl bouncy">🌿</div><div><div className="font-display text-2xl font-bold leading-none">2,400+</div><div className="text-xs text-[#0B0F0C]/60">products tracked</div></div></div>
              <div className="flex items-center gap-2"><div className="text-3xl bouncy" style={{ animationDelay: "-1s" }}>💸</div><div><div className="font-display text-2xl font-bold leading-none">32%</div><div className="text-xs text-[#0B0F0C]/60">avg savings</div></div></div>
              <div className="flex items-center gap-2"><div className="text-3xl bouncy" style={{ animationDelay: "-2s" }}>⭐</div><div><div className="font-display text-2xl font-bold leading-none">18k+</div><div className="text-xs text-[#0B0F0C]/60">smoke tests</div></div></div>
            </div>
          </div>

          {/* Hero side: phone-like deal preview */}
          <div className="lg:col-span-5 relative">
            <div className="card-x card-magenta p-5 bg-[#0B0F0C] text-[#F5F0DC] rotate-[2deg]">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><span className="pulse-dot" /><span className="label-overline text-[#15FF73]">Flash · 8:42</span></div>
                <span className="sticker sticker-lemon">-30%</span>
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden bg-[#1B1B22] mb-4">
                <img src="https://images.unsplash.com/photo-1578908226199-40101eab0ac3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="" className="w-full h-full object-cover" />
              </div>
              <div className="font-display text-3xl font-extrabold tracking-tight leading-none mb-2">Rocky Mountain <span className="text-[#15FF73]">Glue</span></div>
              <div className="flex items-center gap-2 text-xs mb-4">
                <span className="sticker sticker-purple !text-[10px] !py-0.5">Hybrid</span>
                <span className="font-mono-x text-[#15FF73] font-bold">28.7% THC</span>
                <span className="text-[#F5F0DC]/40">·</span>
                <span>4.9 ★ · 78% GAS</span>
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#F5F0DC]/50">Was</div>
                  <div className="font-mono-x line-through text-[#F5F0DC]/40">$50</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#15FF73]">Now</div>
                  <div className="font-display text-4xl font-extrabold text-[#15FF73]">$35</div>
                </div>
                <div className="ml-auto sticker sticker-green !text-[10px]" style={{ transform: "rotate(4deg)" }}>+10 XP</div>
              </div>
            </div>
            {/* Floating sticker on top */}
            <div className="absolute -top-4 -left-6 sticker sticker-magenta !text-sm rotate-[-12deg] shadow-[4px_4px_0_#0B0F0C]">FRESH DROP</div>
            <div className="absolute -bottom-4 -right-4 sticker sticker-cyan !text-sm rotate-[8deg] shadow-[4px_4px_0_#0B0F0C]">CLAIM = QR</div>
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <section className="surface-ink border-y-2 border-[#0B0F0C]">
        <Marquee items={["FRESH DROPS DAILY", "ALL GAS NO BREAKS", "QR CLAIM = WALK IN", "EARN XP, LEVEL UP", "SMOKE TESTED 🔥", "NO MID ALLOWED", "TX · CO · CA · NV", "OOWEE 💨"]} speed={32} />
      </section>

      {/* CATEGORY TILES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-7 gap-4">
          <h2 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight">What's the <span className="text-green-gradient italic">vibe</span>?</h2>
          <Link to="/discover" className="btn-x btn-ghost">All categories →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { cat: "flower", emoji: "🌿", label: "Flower", color: "card-green" },
            { cat: "preroll", emoji: "🚬", label: "Pre-rolls", color: "card-magenta" },
            { cat: "vape", emoji: "💨", label: "Vapes", color: "card-cyan" },
            { cat: "edible", emoji: "🍬", label: "Edibles", color: "card-sunset" },
            { cat: "concentrate", emoji: "🍯", label: "Concentrates", color: "card-purple" },
            { cat: "tincture", emoji: "💧", label: "Tinctures", color: "card-green" },
          ].map((c) => (
            <Link key={c.cat} to={`/discover?category=${c.cat}`} className={`card-x ${c.color} p-5 text-center bg-[#FAF6E8]`}>
              <div className="text-5xl mb-3 bouncy" style={{ animationDelay: `-${Math.random() * 3}s` }}>{c.emoji}</div>
              <div className="font-display text-xl font-extrabold">{c.label}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURE TRIO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-12">
        <div className="grid md:grid-cols-3 gap-5">
          <div className="card-x card-magenta p-7 bg-[#FAF6E8]">
            <div className="text-5xl mb-3">🔥</div>
            <div className="label-overline text-[#FF1A8C] mb-2">Best Value Engine</div>
            <h3 className="font-display text-3xl font-extrabold mb-2 leading-tight">Score every dollar.</h3>
            <p className="text-sm text-[#0B0F0C]/65 leading-relaxed">Price/gram × potency × rating. One number. 0-10. Done.</p>
          </div>
          <div className="card-x card-cyan p-7 bg-[#FAF6E8]">
            <div className="text-5xl mb-3">🎟️</div>
            <div className="label-overline text-[#FF1A8C] mb-2">QR Claim → Walk In</div>
            <h3 className="font-display text-3xl font-extrabold mb-2 leading-tight">Tap. Show. Save.</h3>
            <p className="text-sm text-[#0B0F0C]/65 leading-relaxed">Claim a deal, get a QR, the shop scans it. Closed loop.</p>
          </div>
          <div className="card-x card-sunset p-7 bg-[#FAF6E8]">
            <div className="text-5xl mb-3">🏆</div>
            <div className="label-overline text-[#FF1A8C] mb-2">Level Up</div>
            <h3 className="font-display text-3xl font-extrabold mb-2 leading-tight">Stack XP. Earn rep.</h3>
            <p className="text-sm text-[#0B0F0C]/65 leading-relaxed">Sprout → Bud → Bloom → Connoisseur → Top Shelf. Streaks pay.</p>
          </div>
        </div>
      </section>

      {/* FLASH DEALS */}
      <section className="surface-deepgreen border-y-2 border-[#0B0F0C] py-20 mt-12 relative overflow-hidden">
        <div className="blob" style={{ width: 400, height: 400, background: "#15FF73", top: -100, right: 100, opacity: 0.2 }} />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 relative">
          <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Flame size={20} strokeWidth={2.4} className="text-[#FF7A3D]" />
                <span className="label-overline text-[#15FF73]">Flash deals · expiring</span>
              </div>
              <h2 className="font-display text-5xl sm:text-6xl font-extrabold text-[#F5F0DC] tracking-tight leading-[0.95]">
                Tick tock, <span className="text-[#15FF73]">don't sleep.</span>
              </h2>
            </div>
            <Link to="/deals" className="btn-x btn-light">All deals →</Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {deals.slice(0, 6).map((d, i) => <DealCard key={d.id} d={d} idx={i} />)}
            {deals.length === 0 && <div className="col-span-full text-center text-[#F5F0DC]/60 py-10">No live deals in {stateCode} — switch region 👆</div>}
          </div>
        </div>
      </section>

      {/* TOP VALUE PRODUCTS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <div className="label-overline mb-3 text-[#FF1A8C]">By Value Score</div>
            <h2 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[0.95]">
              The <span className="text-green-gradient italic">bang-for-buck</span> board.
            </h2>
          </div>
          <Link to="/discover" className="btn-x btn-ghost">Browse all →</Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.slice(0, 6).map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* AI CONCIERGE TEASER */}
      <section className="surface-ink border-y-2 border-[#0B0F0C] relative overflow-hidden py-24">
        <div className="blob" style={{ width: 380, height: 380, background: "#FF1A8C", top: -100, left: -100 }} />
        <div className="blob" style={{ width: 320, height: 320, background: "#00E0F5", bottom: -80, right: -60, animationDelay: "-8s" }} />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <div className="label-overline mb-4 text-[#15FF73]">Ask Bud</div>
            <h2 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.95] mb-5 text-[#F5F0DC]">
              "Find me <em className="not-italic text-rainbow">relaxing</em><br />under <span className="text-[#15FF73]">$40.</span>"
            </h2>
            <p className="text-lg text-[#F5F0DC]/70 mb-7 max-w-md leading-relaxed">Voice it. Type it. Tap a vibe. Show Bud a flower pic and he'll read it like a sommelier on a couch. No fluff.</p>
            <Link to="/concierge" className="btn-x btn-primary">Talk to Bud <ArrowRight size={16} /></Link>
          </div>
          <div className="card-dark p-7 rounded-3xl">
            <div className="flex items-center gap-2 mb-5"><Sparkles size={14} className="text-[#15FF73]" /><span className="label-overline text-[#15FF73]">Live demo</span></div>
            <div className="space-y-3">
              <div className="ml-auto max-w-[80%] bg-[#1B1B22] rounded-2xl rounded-tr-sm px-4 py-3 text-sm text-[#F5F0DC]">I want gas under $40, indica-leaning.</div>
              <div className="max-w-[88%] bg-[#15FF73] text-[#0B0F0C] rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed">
                <div className="font-bold mb-1">Top picks in your state:</div>
                • <b>Sunset Sherbet</b> (sleeper) — $28/8th · Value <b>9.1</b><br />
                • <b>Velvet OG</b> — $45/8th · Value <b>8.6</b><br />
                • <b>Boulder Sour Gummies</b> — $22 · Value <b>9.5</b>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GAS / PASS WALL */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
        <div className="text-center mb-10">
          <div className="label-overline text-[#FF1A8C] mb-3">Word on the street</div>
          <h2 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight">
            <span className="text-green-gradient">GAS</span> or <span className="text-[#FF1A8C]">PASS</span>?
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { gas: true, name: "RJ", q: "Velvet OG hit like a freight train. 9/10 don't sleep on it.", strain: "Indica" },
            { gas: true, name: "Mariah", q: "Sunset Sherbet at $28? gas. Bought 2.", strain: "Indica" },
            { gas: false, name: "Tre", q: "Skipped Texas Zkittlez — flavor was mid for $38. PASS.", strain: "Hybrid" },
            { gas: true, name: "Dee", q: "Live Rosin from Mile High is straight loud. Smell hits the parking lot.", strain: "Hybrid" },
            { gas: true, name: "Krys", q: "Pacific Mints carts go crazy for the price. Daytime fave.", strain: "Hybrid" },
            { gas: false, name: "Marco", q: "Pre-roll hemp pack — got the job done but nothing exotic.", strain: "Sativa" },
          ].map((r, i) => (
            <div key={i} className={`card-x ${r.gas ? "card-green" : "card-magenta"} p-6 bg-[#FAF6E8]`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-9 h-9 rounded-full border-2 border-[#0B0F0C] flex items-center justify-center font-display font-bold ${r.gas ? "bg-[#15FF73]" : "bg-[#FF1A8C] text-white"}`}>{r.name[0]}</div>
                  <div>
                    <div className="font-bold text-sm">{r.name}</div>
                    <div className="text-[10px] uppercase tracking-widest font-mono-x text-[#0B0F0C]/50">{r.strain}</div>
                  </div>
                </div>
                <span className={`sticker ${r.gas ? "sticker-green" : "sticker-magenta"} !text-sm`} style={{ transform: "rotate(-4deg)" }}>
                  {r.gas ? "🔥 GAS" : "👎 PASS"}
                </span>
              </div>
              <p className="text-base text-[#0B0F0C] leading-relaxed font-medium">"{r.q}"</p>
            </div>
          ))}
        </div>
      </section>

      {/* MAP / DISPENSARIES TILES */}
      {disps.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-20">
          <div className="flex items-end justify-between mb-7 gap-4">
            <h2 className="font-display text-5xl font-extrabold tracking-tight">Shops near <span className="text-rainbow">{stateCode}</span></h2>
            <Link to="/discover" className="btn-x btn-ghost">View map →</Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {disps.slice(0, 6).map((d, i) => (
              <Link key={d.id} to={`/dispensary/${d.id}`} className={`card-x ${["card-green","card-magenta","card-cyan","card-sunset","card-purple","card-green"][i]} bg-[#FAF6E8] overflow-hidden`}>
                <div className="aspect-[16/9] overflow-hidden bg-[#0B0F0C]"><img src={d.image_url} className="w-full h-full object-cover" alt="" /></div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`sticker ${d.type === "medical" ? "sticker-cyan" : d.type === "hemp" ? "sticker-lemon" : "sticker-magenta"}`}>{d.type}</span>
                    <span className="font-mono-x text-[10px] font-bold text-[#0B0F0C]/60 uppercase tracking-widest">{d.city}, {d.state}</span>
                  </div>
                  <div className="font-display text-2xl font-extrabold leading-tight">{d.name}</div>
                  <div className="flex items-center gap-1 text-xs mt-2"><Star size={12} className="fill-[#FF7A3D] text-[#FF7A3D]" /> <span className="font-mono-x font-bold">{d.rating}</span></div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* PITCH BANNER */}
      <section className="border-y-2 border-[#0B0F0C] surface-cream relative overflow-hidden py-20">
        <div className="blob" style={{ width: 400, height: 400, background: "#FFE74C", top: -120, right: 100 }} />
        <div className="blob" style={{ width: 280, height: 280, background: "#15FF73", bottom: -80, left: -60, animationDelay: "-10s" }} />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <div className="label-overline mb-3 text-[#FF1A8C]">For dispensary owners</div>
            <h2 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.95] mb-5 tracking-tight">
              Pay only when <span className="text-rainbow">it works.</span>
            </h2>
            <p className="text-lg text-[#0B0F0C]/70 mb-7 max-w-xl leading-relaxed">
              Closed-loop revenue. Prepaid wallet. Auto-pause when funds run dry.
              QR scan = verified visit = commission charged. <span className="font-script text-2xl text-[#FF1A8C]">— that simple.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/auth?mode=register&role=dispensary" className="btn-x btn-magenta">List your shop <ArrowRight size={16} /></Link>
              <Link to="/pitch" className="btn-x btn-ghost">Read the pitch →</Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            <div className="card-x card-green p-5 bg-[#FAF6E8]"><div className="text-3xl mb-2">💸</div><div className="font-display text-3xl font-extrabold">$10–15</div><div className="text-xs text-[#0B0F0C]/60 mt-1 leading-tight">per verified visit</div></div>
            <div className="card-x card-magenta p-5 bg-[#FAF6E8]"><div className="text-3xl mb-2">🚀</div><div className="font-display text-3xl font-extrabold">3.4×</div><div className="text-xs text-[#0B0F0C]/60 mt-1 leading-tight">lift on flash SKUs</div></div>
            <div className="card-x card-cyan p-5 bg-[#FAF6E8]"><div className="text-3xl mb-2">🛡️</div><div className="font-display text-3xl font-extrabold">0%</div><div className="text-xs text-[#0B0F0C]/60 mt-1 leading-tight">fees · cancel anytime</div></div>
            <div className="card-x card-sunset p-5 bg-[#FAF6E8]"><div className="text-3xl mb-2">⚡</div><div className="font-display text-3xl font-extrabold">24h</div><div className="text-xs text-[#0B0F0C]/60 mt-1 leading-tight">to onboard</div></div>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section className="max-w-5xl mx-auto px-5 lg:px-10 py-20">
        <WaitlistForm
          kicker="Drops alerts"
          title="Get on the list."
          note="First flash deals, fresh-state launches, founder notes. No spam, easy unsubscribe."
        />
      </section>

      <Footer />
    </div>
  );
}
